import { useRef, useCallback, useEffect } from 'react';
import type { Graph, Edge } from '@antv/x6';
import {
  EDGE_VERTEX_TOOL,
  toggleEdgeCssClass,
  updateOptionArrowVisibility,
  sendEdgeToBack,
} from '../utils/nodeHelpers';
import { isInvocationGuideEdge, getInvocationEdgeStartPoint } from '../utils/mergeSnap';
import { applyInvocationZigzagToEdge, syncInvocationAngleGuides } from '../utils/invocationZigzag';
import { mergeSameTypeEdges, toParentAttach } from '../utils/mergeSameTypeEdges';
import { findMergeSnapTarget, showAllMergeHighlights, showMergeSnapIndicator, hideMergeSnapIndicator, hideAllMergeHighlights } from '../utils/mergeSnap';
import { setSuppressed, isSuppressed } from '../utils/invocationRefresh';

export interface UseEdgeInteractionsOptions {
  graph: Graph | null;
  dragSourceNodeRef: React.MutableRefObject<any>;
  dragTempEdgeRef: React.MutableRefObject<Edge | null>;
  highlightedTargetRef: React.MutableRefObject<any>;
  setDragTargetVisual: (node: any, active: boolean) => void;
  pickHoverTarget: (point: { x: number; y: number }) => any;
  scheduleGuideRefresh: () => void;
  queueInvocationBoundaryRefresh: (edge: Edge) => void;
  setStatusText: (text: string) => void;
}

export function useEdgeInteractions({
  graph,
  dragSourceNodeRef,
  dragTempEdgeRef,
  highlightedTargetRef,
  setDragTargetVisual,
  pickHoverTarget,
  scheduleGuideRefresh,
  queueInvocationBoundaryRefresh,
  setStatusText,
}: UseEdgeInteractionsOptions) {
  const stableVertexTool = {
    ...EDGE_VERTEX_TOOL,
    args: {
      ...(EDGE_VERTEX_TOOL as any).args,
      addable: true,
      removable: false,
    },
  };

  const selectedEdgeRef = useRef<Edge | null>(null);
  const hoveredEdgeRef = useRef<Edge | null>(null);
  const edgeLeaveTimerRef = useRef<number | null>(null);

  const mergeDragEdgeRef = useRef<Edge | null>(null);
  const mergeSnapTargetRef = useRef<{ x: number; y: number; edgeId: string } | null>(null);
  const mergeSnapIndicatorRef = useRef<SVGElement | null>(null);
  const mergeHighlightRef = useRef<SVGElement | null>(null);

  const edgeEndpointDragRef = useRef<Edge | null>(null);
  const edgeEndpointDragPosRef = useRef<{ x: number; y: number } | null>(null);
  const edgeEndpointDragSideRef = useRef<'source' | 'target' | null>(null);

  const clearSelectedEdge = useCallback(() => {
    if (!graph) return;
    if (selectedEdgeRef.current) {
      toggleEdgeCssClass(selectedEdgeRef.current, graph, 'edge-selected', false);
      selectedEdgeRef.current = null;
    }
  }, [graph]);

  const selectEdge = useCallback((edge: Edge | null) => {
    if (!edge || !graph) return;
    if (selectedEdgeRef.current?.id === edge.id) return;
    clearSelectedEdge();
    toggleEdgeCssClass(edge, graph, 'edge-selected', true);
    selectedEdgeRef.current = edge;
  }, [graph, clearSelectedEdge]);

  const createEdgeRemoveTool = useCallback(() => ({
    name: 'button-remove',
    args: {
      distance: 0.7,
      rotate: false,
      offset: 0,
      markup: [
        {
          tagName: 'circle',
          selector: 'button',
          attrs: {
            r: 9,
            fill: '#ff4d4f',
            stroke: '#fff',
            'stroke-width': 2,
            cursor: 'pointer',
          },
        },
        {
          tagName: 'path',
          selector: 'icon',
          attrs: {
            d: 'M -3 -3 3 3 M -3 3 3 -3',
            fill: 'none',
            stroke: '#fff',
            'stroke-width': 2,
            'pointer-events': 'none',
          },
        },
      ],
      onClick: ({ edge, view }: { edge?: Edge; view?: any }) => {
        if (!graph) return;
        const targetEdge: Edge | undefined =
          hoveredEdgeRef.current || selectedEdgeRef.current || edge || view?.cell;
        if (!targetEdge) return;
        const edgeData = targetEdge.getData?.() || {};
        const targetId = targetEdge.getTargetCellId();
        const subtype = edgeData.edgeSubtype;
        if (edgeData._mergedEdge && targetId && subtype) {
          const mergedEdges = graph.getEdges().filter(e => {
            const d = e.getData?.() || {};
            return d.edgeSubtype === subtype && e.getTargetCellId() === targetId;
          });
          mergedEdges.forEach(e => graph.removeCell(e.id));
        } else {
          graph.removeCell(targetEdge.id);
        }
      },
    },
  }), [graph]);

  const addEdgeTools = useCallback((edge: any) => {
    if (!graph) return;
    if (isInvocationGuideEdge(edge)) return;
    const edgeData = edge.getData?.() || {};
    const isInvocation = edgeData.edgeSubtype === 'invocation';
    const tools: any[] = [];

    if (!edge.hasTool('vertices')) {
      tools.push(stableVertexTool);
    }
    if (isInvocation) {
      if (!edge.hasTool('source-arrowhead')) {
        tools.push({
          name: 'source-arrowhead',
          args: { attrs: { fill: '#1890ff', stroke: '#fff', strokeWidth: 1, r: 5 } },
        });
      }
      if (!edge.hasTool('target-arrowhead')) {
        tools.push({
          name: 'target-arrowhead',
          args: { attrs: { fill: '#1890ff', stroke: '#fff', strokeWidth: 1, r: 5 } },
        });
      }
    }
    if (!edge.hasTool('button-remove') && !edge.hasTool('remove-button')) {
      tools.push(createEdgeRemoveTool());
    }

    if (tools.length > 0) {
      edge.addTools(tools);
    }
  }, [graph, createEdgeRemoveTool, stableVertexTool]);

  // 点击选中后才加蓝点工具
  const addEdgeVertexTools = useCallback((edge: any) => {
    if (!graph) return;
    if (isInvocationGuideEdge(edge)) return;
    const edgeData = edge.getData?.() || {};
    const isInvocation = edgeData.edgeSubtype === 'invocation';
    const tools: any[] = [];

    if (!edge.hasTool('vertices')) {
      tools.push(stableVertexTool);
    }
    if (isInvocation) {
      if (!edge.hasTool('source-arrowhead')) {
        tools.push({
          name: 'source-arrowhead',
          args: { attrs: { fill: '#1890ff', stroke: '#fff', strokeWidth: 1, r: 5 } },
        });
      }
      if (!edge.hasTool('target-arrowhead')) {
        tools.push({
          name: 'target-arrowhead',
          args: { attrs: { fill: '#1890ff', stroke: '#fff', strokeWidth: 1, r: 5 } },
        });
      }
    }

    if (tools.length > 0) {
      edge.addTools(tools);
    }
  }, [graph, stableVertexTool]);

  const removeEdgeTools = useCallback((edge: any) => {
    edge.removeTools();
  }, []);

  const handleMergeMouseUp = useCallback((): void => {
    if (!graph) return;
    const edge = mergeDragEdgeRef.current;
    const snap = mergeSnapTargetRef.current;
    const edgeData = edge?.getData?.() || {};
    const sourceId = edge?.getSourceCellId?.() || edgeData._invocationSourceNodeId;
    const targetId = edge?.getTargetCellId?.();
    const srcTerminal = edge?.getSource?.() as any;
    const tgtTerminal = edge?.getTarget?.() as any;
    const dragSide = edgeEndpointDragSideRef.current;
    const isDraggingSource = dragSide === 'source' || (typeof srcTerminal?.x === 'number' && !srcTerminal?.cell);
    const isDraggingTarget = dragSide === 'target' || (typeof tgtTerminal?.x === 'number' && !tgtTerminal?.cell);
    const hoverTargetId = highlightedTargetRef.current?.id;
    const shouldUseEndpointAttach = !!edge &&
      edgeEndpointDragRef.current?.id === edge.id &&
      !!hoverTargetId && (
        (isDraggingSource && hoverTargetId !== sourceId) ||
        (isDraggingTarget && hoverTargetId !== targetId)
      );
    if (edge) {
      const src = edge.getSource?.() as any;
      const endpointDragStart = edgeEndpointDragRef.current?.id === edge.id ? edgeEndpointDragPosRef.current : null;
      const capturedStart = snap
        ? { x: snap.x, y: snap.y }
        : typeof src?.x === 'number' && !src?.cell
          ? { x: src.x, y: src.y }
          : endpointDragStart
            ? { x: endpointDragStart.x, y: endpointDragStart.y }
            : null;
      if (!shouldUseEndpointAttach) {
        setSuppressed(true);
        setTimeout(() => {
          const latestEdgeData = edge.getData?.() || {};
          const currentSourceId = edge.getSourceCellId?.() || sourceId || latestEdgeData._invocationSourceNodeId;
          const currentTargetId = edge.getTargetCellId?.() || targetId;
          const sourceNode = currentSourceId ? (graph.getCellById(currentSourceId) as any) : null;
          const targetNode = currentTargetId ? (graph.getCellById(currentTargetId) as any) : null;
          const newStart = capturedStart;

          if (newStart && sourceNode?.isNode?.() && targetNode?.isNode?.()) {
            const sBox = sourceNode.getBBox();
            const sCx = sBox.x + sBox.width / 2;
            const sCy = sBox.y + sBox.height / 2;
            edge.setSource({
              cell: sourceNode.id,
              anchor: { name: 'invocation-source' },
              connectionPoint: { name: 'anchor' },
            });
            edge.setData({
              ...latestEdgeData,
              _invocationSourceNodeId: sourceNode.id,
              _fixedStartOffset: {
                dx: newStart.x - sCx,
                dy: newStart.y - sCy,
              },
              _fixedStartForTargetId: currentTargetId,
              _fixedStartManual: true,
            });
          }
          syncInvocationAngleGuides(graph);
          setSuppressed(false);
        }, 0);
      }
    }
    mergeDragEdgeRef.current = null;
    mergeSnapTargetRef.current = null;

    if (!shouldUseEndpointAttach && edgeEndpointDragRef.current?.id === edge?.id) {
      edgeEndpointDragRef.current = null;
      edgeEndpointDragPosRef.current = null;
      edgeEndpointDragSideRef.current = null;
    }

    hideMergeSnapIndicator(mergeSnapIndicatorRef);
    hideAllMergeHighlights(mergeHighlightRef);

    const edgeEndpoint = edgeEndpointDragRef.current;
    const edgeDropPos = edgeEndpointDragPosRef.current;
    if (edgeEndpoint && edgeDropPos) {
      edgeEndpointDragRef.current = null;
      edgeEndpointDragPosRef.current = null;
      const draggedSide = edgeEndpointDragSideRef.current;
      edgeEndpointDragSideRef.current = null;

      if (highlightedTargetRef.current) {
        setDragTargetVisual(highlightedTargetRef.current, false);
        highlightedTargetRef.current = null;
      }

      const src = edgeEndpoint.getSource?.() as any;
      const tgt = edgeEndpoint.getTarget?.() as any;
      const isDraggingTarget = draggedSide === 'target' || (typeof tgt?.x === 'number' && !tgt?.cell);
      const isDraggingSource = draggedSide === 'source' || (typeof src?.x === 'number' && !src?.cell);

      const otherNodeId = isDraggingTarget
        ? edgeEndpoint.getSourceCellId?.()
        : edgeEndpoint.getTargetCellId?.();
      const otherNode = otherNodeId ? graph.getCellById(otherNodeId) : null;
      const savedSource = dragSourceNodeRef.current;
      dragSourceNodeRef.current = otherNode as any;
      const hoverTarget = pickHoverTarget(edgeDropPos);
      dragSourceNodeRef.current = savedSource;

      if (hoverTarget) {
        const edgeData = edgeEndpoint.getData?.() || {};
        const attach = toParentAttach(hoverTarget, edgeDropPos);

        setSuppressed(true);
        if (isDraggingTarget) {
          edgeEndpoint.setData({
            ...edgeData,
            _parentAttachSide: attach.side,
            _parentAttachRatio: attach.ratio,
            _manualVertices: false,
          });
          edgeEndpoint.setTarget({
            cell: hoverTarget.id,
            connectionPoint: { name: 'boundary' },
          });
        } else if (isDraggingSource) {
          edgeEndpoint.setData({
            ...edgeData,
            _parentAttachSide: attach.side,
            _parentAttachRatio: attach.ratio,
            _manualVertices: false,
          });
          edgeEndpoint.setSource({
            cell: hoverTarget.id,
            connectionPoint: { name: 'boundary' },
          });
        }
        mergeSameTypeEdges(graph);
        syncInvocationAngleGuides(graph);
        setSuppressed(false);
      } else {
        setSuppressed(true);
        mergeSameTypeEdges(graph);
        syncInvocationAngleGuides(graph);
        setSuppressed(false);
      }
    } else {
      if (highlightedTargetRef.current) {
        setDragTargetVisual(highlightedTargetRef.current, false);
        highlightedTargetRef.current = null;
      }
      edgeEndpointDragRef.current = null;
      edgeEndpointDragPosRef.current = null;
      edgeEndpointDragSideRef.current = null;
    }
  }, [graph, dragSourceNodeRef, dragTempEdgeRef, highlightedTargetRef, setDragTargetVisual, pickHoverTarget]);

  useEffect(() => {
    if (!graph) return;

    const containerElement = graph.container;
    const updateCursor = (value: string) => {
      if (containerElement) containerElement.style.cursor = value;
    };

    graph.getEdges().forEach((edge: any) => sendEdgeToBack(edge, graph));

    const handleEdgeAdded = ({ edge }: { edge: Edge }) => sendEdgeToBack(edge, graph);
    graph.on('edge:added', handleEdgeAdded);

    const handleNodeChangeSize = ({ node }: { node: any }) => {
      if (isSuppressed()) return;
      const connectedEdges = graph.getConnectedEdges(node);
      connectedEdges.forEach((edge: any) => {
        const edgeData = edge.getData?.() || {};
        if (edgeData.edgeSubtype === 'invocation') {
          queueInvocationBoundaryRefresh(edge);
        }
      });
      setSuppressed(true);
      mergeSameTypeEdges(graph);
      setSuppressed(false);
    };
    graph.on('node:change:size', handleNodeChangeSize);

    const handleNodeChangePosition = ({ node }: { node: any }) => {
      if (isSuppressed()) return;
      const connectedEdges = graph.getConnectedEdges(node);
      if (connectedEdges.some((edge: any) => {
        const edgeData = edge.getData?.() || {};
        return edgeData.edgeSubtype === 'invocation';
      })) {
        scheduleGuideRefresh();
      }
    };
    graph.on('node:change:position', handleNodeChangePosition);

    const handleEdgeChangeVertices = ({ edge }: { edge: any }) => {
      if (isSuppressed()) return;
      if (isInvocationGuideEdge(edge)) return;
      const data = edge.getData?.() || {};
      if (!data._manualVertices) {
        edge.setData({ ...data, _manualVertices: true });
      }
    };
    graph.on('edge:change:vertices', handleEdgeChangeVertices);

    const handleEdgeChangeSource = ({ edge }: { edge: any }) => {
      if (isSuppressed()) return;
      if (edge === dragTempEdgeRef.current) return;
      if (isInvocationGuideEdge(edge)) return;
      const data = edge.getData?.() || {};
      const src = edge.getSource?.() as any;
      if (typeof src?.x === 'number' && !src?.cell) {
        if (!mergeDragEdgeRef.current) {
          mergeDragEdgeRef.current = edge;
          showAllMergeHighlights(graph, edge, mergeHighlightRef);
        }
        const snap = findMergeSnapTarget(graph, edge, { x: src.x, y: src.y });
        if (snap) {
          mergeSnapTargetRef.current = snap;
          showMergeSnapIndicator(graph, snap, mergeSnapIndicatorRef);
        } else {
          mergeSnapTargetRef.current = null;
          hideMergeSnapIndicator(mergeSnapIndicatorRef);
        }

        edgeEndpointDragRef.current = edge;
        edgeEndpointDragPosRef.current = { x: src.x, y: src.y };
        edgeEndpointDragSideRef.current = 'source';
        const targetId = edge.getTargetCellId?.();
        const targetNode = targetId ? graph.getCellById(targetId) : null;
        const savedSource = dragSourceNodeRef.current;
        dragSourceNodeRef.current = targetNode as any;
        const hoverTarget = pickHoverTarget({ x: src.x, y: src.y });
        dragSourceNodeRef.current = savedSource;

        if (hoverTarget !== highlightedTargetRef.current) {
          if (highlightedTargetRef.current) setDragTargetVisual(highlightedTargetRef.current, false);
          if (hoverTarget) setDragTargetVisual(hoverTarget, true);
          highlightedTargetRef.current = hoverTarget;
        }

        setSuppressed(true);
        syncInvocationAngleGuides(graph);
        setSuppressed(false);
        return;
      }

      if (data.edgeSubtype === 'invocation' && edgeEndpointDragRef.current?.id === edge.id) {
        edgeEndpointDragSideRef.current = 'source';
        if (!edgeEndpointDragPosRef.current) {
          const startPoint = getInvocationEdgeStartPoint(edge as Edge);
          if (startPoint) {
            edgeEndpointDragPosRef.current = { x: startPoint.x, y: startPoint.y };
          }
        }
        const dragPos = edgeEndpointDragPosRef.current;
        if (dragPos) {
          const targetId = edge.getTargetCellId?.();
          const targetNode = targetId ? graph.getCellById(targetId) : null;
          const savedSource = dragSourceNodeRef.current;
          dragSourceNodeRef.current = targetNode as any;
          const hoverTarget = pickHoverTarget(dragPos);
          dragSourceNodeRef.current = savedSource;

          if (hoverTarget !== highlightedTargetRef.current) {
            if (highlightedTargetRef.current) setDragTargetVisual(highlightedTargetRef.current, false);
            if (hoverTarget) setDragTargetVisual(hoverTarget, true);
            highlightedTargetRef.current = hoverTarget;
          }
        }
        return;
      }

      if (data._manualVertices) return;
      if (data.edgeSubtype === 'invocation') {
        setSuppressed(true);
        applyInvocationZigzagToEdge(edge, graph, true);
        setSuppressed(false);
        scheduleGuideRefresh();
      }
    };
    graph.on('edge:change:source', handleEdgeChangeSource);

    const handleEdgeChangeTarget = ({ edge }: { edge: any }) => {
      if (isSuppressed()) return;
      if (edge === dragTempEdgeRef.current) return;
      if (isInvocationGuideEdge(edge)) return;
      const data = edge.getData?.() || {};
      const tgt = edge.getTarget?.() as any;
      if (typeof tgt?.x === 'number' && !tgt?.cell) {
        edgeEndpointDragRef.current = edge;
        edgeEndpointDragPosRef.current = { x: tgt.x, y: tgt.y };
        edgeEndpointDragSideRef.current = 'target';

        const sourceId = edge.getSourceCellId?.();
        const sourceNode = sourceId ? graph.getCellById(sourceId) : null;
        const savedSource = dragSourceNodeRef.current;
        dragSourceNodeRef.current = sourceNode as any;
        const hoverTarget = pickHoverTarget({ x: tgt.x, y: tgt.y });
        dragSourceNodeRef.current = savedSource;

        if (hoverTarget !== highlightedTargetRef.current) {
          if (highlightedTargetRef.current) setDragTargetVisual(highlightedTargetRef.current, false);
          if (hoverTarget) setDragTargetVisual(hoverTarget, true);
          highlightedTargetRef.current = hoverTarget;
        }

        setSuppressed(true);
        syncInvocationAngleGuides(graph);
        setSuppressed(false);
        return;
      }
      if (data._manualVertices) return;
      if (data.edgeSubtype === 'invocation') {
        setSuppressed(true);
        applyInvocationZigzagToEdge(edge, graph, true);
        setSuppressed(false);
        scheduleGuideRefresh();
      }
    };
    graph.on('edge:change:target', handleEdgeChangeTarget);

    const handleEdgeMouseEnter = ({ edge }: { edge: any }) => {
      if (isInvocationGuideEdge(edge)) return;
      if (edgeLeaveTimerRef.current !== null) {
        clearTimeout(edgeLeaveTimerRef.current);
        edgeLeaveTimerRef.current = null;
      }
      if (hoveredEdgeRef.current && hoveredEdgeRef.current.id !== edge.id) {
        toggleEdgeCssClass(hoveredEdgeRef.current, graph, 'edge-hover', false);
        removeEdgeTools(hoveredEdgeRef.current);
      }
      if (hoveredEdgeRef.current?.id === edge.id) return;
      hoveredEdgeRef.current = edge;
      toggleEdgeCssClass(edge, graph, 'edge-hover', true);
      addEdgeTools(edge);
      updateCursor('pointer');
    };
    graph.on('edge:mouseenter', handleEdgeMouseEnter);

    const handleEdgeMouseLeave = ({ edge, e }: { edge: any; e: MouseEvent }) => {
      if (isInvocationGuideEdge(edge)) return;
      const related = e?.relatedTarget as HTMLElement | null;
      const withinTools = !!related && !!related.closest?.('.x6-widget-tools, .x6-edge-tools');
      if (withinTools) return;
      if (edgeLeaveTimerRef.current !== null) {
        clearTimeout(edgeLeaveTimerRef.current);
      }
      edgeLeaveTimerRef.current = window.setTimeout(() => {
        if (hoveredEdgeRef.current?.id === edge.id) {
          hoveredEdgeRef.current = null;
        }
        toggleEdgeCssClass(edge, graph, 'edge-hover', false);
        removeEdgeTools(edge);
        updateCursor('default');
        edgeLeaveTimerRef.current = null;
      }, 300);
    };
    graph.on('edge:mouseleave', handleEdgeMouseLeave);

    const handleEdgeClick = ({ edge, e }: { edge: Edge; e: MouseEvent }) => {
      if (isInvocationGuideEdge(edge)) return;
      e.stopPropagation();
      selectEdge(edge);
      addEdgeVertexTools(edge);
      setStatusText(`选中连线: ${edge.getData()?.label || edge.id}`);
    };
    graph.on('edge:click', handleEdgeClick);

    const handleEdgeDblClick = ({ edge }: { edge: any }) => {
      if (isInvocationGuideEdge(edge)) return;
      setStatusText(`边: ${edge.getData()?.label || edge.id}`);
    };
    graph.on('edge:dblclick', handleEdgeDblClick);

    const handleEdgeRemoved = ({ edge }: { edge: any }) => {
      const edgeData = edge.getData?.() || {};
      if (edgeData.edgeSubtype === 'invocation' && !edgeData._invocationAngleGuide) {
        scheduleGuideRefresh();
      }
      const source = edge.getSource?.();
      const target = edge.getTarget?.();
      const srcPort = source?.port;
      const tgtPort = target?.port;
      const srcId = typeof source === 'string' ? source : source?.cell;
      const tgtId = typeof target === 'string' ? target : target?.cell;
      const srcCell = srcId ? graph.getCellById(srcId) : edge.getSourceCell?.();
      const tgtCell = tgtId ? graph.getCellById(tgtId) : edge.getTargetCell?.();
      if (srcPort === 'port-arrow' && srcCell) updateOptionArrowVisibility(srcCell, graph);
      if (tgtPort === 'port-arrow' && tgtCell) updateOptionArrowVisibility(tgtCell, graph);
      if (selectedEdgeRef.current?.id === edge.id) {
        selectedEdgeRef.current = null;
      }
    };
    graph.on('edge:removed', handleEdgeRemoved);

    window.addEventListener('mouseup', handleMergeMouseUp);
    window.addEventListener('touchend', handleMergeMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMergeMouseUp);
      window.removeEventListener('touchend', handleMergeMouseUp);
      hideMergeSnapIndicator(mergeSnapIndicatorRef);
      hideAllMergeHighlights(mergeHighlightRef);
      graph.off('edge:added', handleEdgeAdded);
      graph.off('node:change:size', handleNodeChangeSize);
      graph.off('node:change:position', handleNodeChangePosition);
      graph.off('edge:change:vertices', handleEdgeChangeVertices);
      graph.off('edge:change:source', handleEdgeChangeSource);
      graph.off('edge:change:target', handleEdgeChangeTarget);
      graph.off('edge:mouseenter', handleEdgeMouseEnter);
      graph.off('edge:mouseleave', handleEdgeMouseLeave);
      graph.off('edge:click', handleEdgeClick);
      graph.off('edge:dblclick', handleEdgeDblClick);
      graph.off('edge:removed', handleEdgeRemoved);
    };
  }, [graph, dragSourceNodeRef, dragTempEdgeRef, highlightedTargetRef, setDragTargetVisual, pickHoverTarget, scheduleGuideRefresh, queueInvocationBoundaryRefresh, setStatusText, selectEdge, addEdgeTools, removeEdgeTools, handleMergeMouseUp]);

  return {
    selectedEdgeRef,
    hoveredEdgeRef,
    edgeEndpointDragRef,
    edgeEndpointDragPosRef,
    mergeDragEdgeRef,
    mergeSnapTargetRef,
    mergeSnapIndicatorRef,
    mergeHighlightRef,
    clearSelectedEdge,
    selectEdge,
    addEdgeTools,
    removeEdgeTools,
  };
}
